import { Emitter } from "@core/events/emitter";
import { Ioc } from "@core/ioc/ioc";
import { type AssetDto, assetObjectKey } from "@models/data/assetDto";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import type { IProjectListModel } from "@models/project/IProjectListModel";
import { PROJECT_LIST_MODEL } from "@models/project/IProjectListModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import { STORAGE_MODEL } from "@models/storage/IStorageModel";
import type { AssetLibraryItem, IAssetLibraryModel } from "./IAssetLibraryModel";

/**
 * `AssetLibraryModel` — see {@link IAssetLibraryModel} for the ownership
 * note (Unity hosts this on `CustomerModel`).
 */
export class AssetLibraryModel implements IAssetLibraryModel {
  readonly onChanged = new Emitter<ReadonlyArray<AssetLibraryItem>>();
  readonly onBusyChanged = new Emitter<boolean>();

  private m_items: ReadonlyArray<AssetLibraryItem> = [];
  private m_isBusy = false;
  /** assetId → preview blob URL (`null` = probed, none exists). */
  private readonly m_previewUrls = new Map<string, string | null>();
  private readonly m_previewLoads = new Map<string, Promise<string | null>>();
  /** assetId → objectKey, retained for the delete cascade. */
  private readonly m_dtoById = new Map<string, AssetDto>();

  get items(): ReadonlyArray<AssetLibraryItem> {
    return this.m_items;
  }

  get isBusy(): boolean {
    return this.m_isBusy;
  }

  async loadAsync(signal?: AbortSignal): Promise<ReadonlyArray<AssetLibraryItem>> {
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const customerId = auth.currentUser?.customerId;
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    if (!customerId || !storage.isOnline) {
      this.setItems([]);
      return this.m_items;
    }

    this.setBusy(true);
    try {
      const dtos = await storage.getAllAssets(customerId, signal);
      const dependents = this.buildDependentProjectMap();
      const dependentsTrustworthy = dependents.size > 0;

      this.m_dtoById.clear();
      const items: AssetLibraryItem[] = [];
      for (const dto of dtos) {
        if (!dto.ID) continue;
        // Unity's library filters: `Version == 0` means the asset was never
        // saved to the library, and licensing-restricted assets are hidden.
        if ((dto.AssetVersion ?? 0) === 0) continue;
        if (dto.HasLicensingRestrictions === true) continue;

        this.m_dtoById.set(dto.ID, dto);
        const projectNames = [...(dependents.get(dto.ID) ?? new Set<string>())];
        const lastModifiedMs = parseLastModified(dto.LastModified);
        items.push({
          id: dto.ID,
          // Dynamo attr is `Name` (NOT `FileName`) — `ToAssetItem` maps
          // `AssetData.FileName` onto it; the DTO exposes both spellings.
          name:
            (typeof dto.Name === "string" && dto.Name) || dto.FileName || "Untitled",
          objectKey: assetObjectKey(dto),
          format: dto.Format ?? "",
          // Unity read fixup: a persisted Scale of 0 means 1.
          scale: dto.Scale && dto.Scale > 0 ? dto.Scale : 1,
          fileSize: dto.FileSize ?? 0,
          triangleCount: dto.TriangleCount ?? 0,
          userTag: dto.UserTag ?? "",
          lastModifiedMs,
          previewCacheKey: `${dto.ID}|${lastModifiedMs}`,
          dependentProjectNames: projectNames,
          // FAIL SAFE: the dependents map is only as good as the project-row
          // `AssetIDs` lists, which the web port does not yet write (it
          // persists scene-level `AssetIDs` only). If NO project row reports
          // any asset ids at all, the map is untrustworthy rather than
          // genuinely empty — refuse deletion instead of destroying the part
          // rows + S3 binary of an asset that is actually in use.
          canRemove: dependentsTrustworthy && projectNames.length === 0,
        });
      }
      // Unity sorts DESC by parsed `LastModified` in the view model (its
      // CustomerModel pre-sort is a string sort — a documented no-op quirk).
      items.sort((a, b) => b.lastModifiedMs - a.lastModifiedMs);
      this.setItems(items);
    } catch (err) {
      console.warn("[AssetLibraryModel] loadAsync failed:", err);
      this.setItems([]);
    } finally {
      this.setBusy(false);
    }
    return this.m_items;
  }

  async getPreviewUrlAsync(assetId: string, cacheKey?: string): Promise<string | null> {
    if (!assetId) return null;
    // Cache on the caller's key (`{id}|{lastModifiedMs}`) so re-saving an asset
    // to the library — which overwrites `assetpreview/{assetId}` in place —
    // doesn't keep serving the previous version's blob.
    const key = cacheKey ?? assetId;
    const cached = this.m_previewUrls.get(key);
    if (cached !== undefined) return cached;
    const inFlight = this.m_previewLoads.get(key);
    if (inFlight) return inFlight;

    const load = (async () => {
      const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
      const customerId = auth.currentUser?.customerId;
      if (!customerId) return null;
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      const url = await storage.getAssetPreviewBlobUrl(customerId, assetId);
      this.m_previewUrls.set(key, url);
      return url;
    })();
    this.m_previewLoads.set(key, load);
    try {
      return await load;
    } finally {
      this.m_previewLoads.delete(key);
    }
  }

  async deleteAsync(assetId: string): Promise<void> {
    const item = this.m_items.find((i) => i.id === assetId);
    if (!item) return;
    if (!item.canRemove) {
      // Unity throws `InvalidOperationException`; the UI already hides the
      // affordance, so treat a stray call as a no-op with a warning.
      console.warn(
        `[AssetLibraryModel] refusing to delete ${assetId}: used by ${item.dependentProjectNames.length} project(s)`,
      );
      return;
    }
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const customerId = auth.currentUser?.customerId;
    if (!customerId) return;
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);

    // Unity order (`ProjectDataModel.DeleteAssetAsync`): part rows → asset
    // row → S3 binary → S3 preview. S3 deletes tolerate a missing key.
    try {
      const parts = await storage.getParts(customerId, assetId);
      const partIds = parts.map((p) => p.ID).filter(Boolean);
      if (partIds.length > 0) await storage.deleteParts(customerId, assetId, partIds);
    } catch (err) {
      console.warn(`[AssetLibraryModel] part cleanup failed for ${assetId}:`, err);
    }
    await storage.deleteAsset(customerId, assetId);
    await storage.deleteAssetObject(customerId, item.objectKey);
    await storage.deleteAssetPreviewObject(customerId, assetId);

    const url = this.m_previewUrls.get(assetId);
    if (url) URL.revokeObjectURL(url);
    this.m_previewUrls.delete(assetId);
    this.m_dtoById.delete(assetId);
    this.setItems(this.m_items.filter((i) => i.id !== assetId));
  }

  reset(): void {
    for (const url of this.m_previewUrls.values()) {
      if (url) URL.revokeObjectURL(url);
    }
    this.m_previewUrls.clear();
    this.m_previewLoads.clear();
    this.m_dtoById.clear();
    this.setItems([]);
  }

  /**
   * Port of `CustomerModel.BuildAssetIdToProjectMap`: union every account
   * project row's `AssetIDs` into `assetId → project names`. Unity walks ALL
   * account projects — including other users' unshared ones (C3D-7722) —
   * which is exactly what the cached project list holds.
   */
  private buildDependentProjectMap(): Map<string, Set<string>> {
    // Keyed by assetId → set of project NAMES, but de-duplicated by project
    // ID upstream: two distinct projects can share a name (the drafts default
    // naming makes that likely), and de-duping by name would undercount.
    const map = new Map<string, Set<string>>();
    if (!Ioc.isRegistered<IProjectListModel>(PROJECT_LIST_MODEL)) return map;
    const projects = Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL).projects;
    const seenPerAsset = new Map<string, Set<string>>();
    for (const project of projects) {
      for (const assetId of new Set(project.assetIds ?? [])) {
        let seenIds = seenPerAsset.get(assetId);
        if (!seenIds) {
          seenIds = new Set<string>();
          seenPerAsset.set(assetId, seenIds);
        }
        if (seenIds.has(project.id)) continue;
        seenIds.add(project.id);
        const names = map.get(assetId);
        if (names) names.add(project.name);
        else map.set(assetId, new Set([project.name]));
      }
    }
    return map;
  }

  private setItems(items: ReadonlyArray<AssetLibraryItem>): void {
    this.m_items = items;
    this.onChanged.emit(items);
  }

  private setBusy(busy: boolean): void {
    if (this.m_isBusy === busy) return;
    this.m_isBusy = busy;
    this.onBusyChanged.emit(busy);
  }
}

/**
 * `AssetData.LastModified` is written as
 * `DateTime.UtcNow.ToString(InvariantCulture)` — e.g. `"07/29/2026 14:03:22"`
 * — which `Date.parse` handles as US-format local time. ISO strings (what
 * the web writes) parse too. Unparseable → 0, sorting the row last, matching
 * Unity's `DateTime.MinValue` fallback in `AssetInfo.LastModifiedUtc`.
 */
function parseLastModified(value: string | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
